import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import type { InviteUser, User, UserRole } from "../../../../common/src";
import { inviteUserSchema, userRoleSchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { trpc } from "../../trpc";
import { toFieldErrors } from "../../utils/fieldErrors";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { useFormStyles } from "../shared/styles";

/** Invite someone by email; they choose their own name and password from the emailed link. */
export const InviteUserForm = ({
  onInvited,
}: {
  onInvited: (user: User) => void;
}) => {
  const form = useFormStyles();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("veteran");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sent, setSent] = useState<string | undefined>(undefined);
  const invite = useAction(
    useCallback((input: InviteUser) => trpc.user.invite.mutate(input), [])
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSent(undefined);
    const parsed = inviteUserSchema.safeParse({ email, role });
    if (!parsed.success) {
      setErrors(toFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    const user = await invite.run(parsed.data);
    if (user) {
      setSent(`Invitation sent to ${user.email}. The link is good for 7 days.`);
      setEmail("");
      setRole("veteran");
      onInvited(user);
    }
  };

  return (
    <Card title="Invite someone">
      {sent && (
        <Notice kind="success" onDismiss={() => setSent(undefined)}>
          {sent}
        </Notice>
      )}
      {invite.error && (
        <Notice kind="error" onDismiss={invite.clearError}>
          {invite.error}
        </Notice>
      )}
      <form onSubmit={(e) => void submit(e)} noValidate>
        <div className={form.grid}>
          <Field
            label="Email address"
            htmlFor="invite-email"
            hint="They'll get a link to set a password and sign the waiver."
            error={errors.email}
          >
            <input
              id="invite-email"
              type="email"
              className={form.control}
              autoCapitalize="none"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Role" htmlFor="invite-role" error={errors.role}>
            <select
              id="invite-role"
              className={form.control}
              value={role}
              onChange={(e) => {
                const parsed = userRoleSchema.safeParse(e.target.value);
                if (parsed.success) {
                  setRole(parsed.data);
                }
              }}
            >
              <option value="veteran">Veteran</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
        </div>
        <div className={form.actions}>
          <Button type="submit" disabled={invite.pending || !email}>
            {invite.pending ? "Sending…" : "Send invitation"}
          </Button>
        </div>
      </form>
    </Card>
  );
};
